import { useCallback } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import {
  FIRESTORE_LOYALTY_USERS_TABLE,
  FIRESTORE_LOYALTY_CLAIMS_TABLE,
  FIRESTORE_LOYALTY_REWARDS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "../../constants/FirestoreConstant";

/**
 * Get the correct Firestore instance for a shopSource
 */
const getFirestore = (shopSource) => {
  return shopSource === "HVO" ? hvoFirestore : vceFirestore;
};

/**
 * Fetcher: loyalty users from both VCE and HVO, enriched with email from webshopUsers
 */
const fetchLoyaltyUsers = async () => {
  // Fetch loyalty users from both databases
  const [vceSnapshot, hvoSnapshot] = await Promise.all([
    vceFirestore.collection(FIRESTORE_LOYALTY_USERS_TABLE).get(),
    hvoFirestore.collection(FIRESTORE_LOYALTY_USERS_TABLE).get(),
  ]);

  const vceLoyaltyUsers = vceSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "VCE",
  }));

  const hvoLoyaltyUsers = hvoSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "HVO",
  }));

  const allLoyaltyUsers = [...vceLoyaltyUsers, ...hvoLoyaltyUsers];

  // Fetch webshop users from both databases to get email addresses
  const [vceUsersSnapshot, hvoUsersSnapshot] = await Promise.all([
    vceFirestore.collection(FIRESTORE_USERS_TABLE).where("role", "==", "webshopUser").get(),
    hvoFirestore.collection(FIRESTORE_USERS_TABLE).where("role", "==", "webshopUser").get(),
  ]);

  const userEmailMap = {};
  vceUsersSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    userEmailMap[`VCE_${doc.id}`] = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    };
  });
  hvoUsersSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    userEmailMap[`HVO_${doc.id}`] = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    };
  });

  // Enrich loyalty users with email data
  return allLoyaltyUsers.map((user) => {
    const webshopUser = userEmailMap[`${user.shopSource}_${user.id}`];
    return {
      ...user,
      email: webshopUser?.email || null,
      firstName: webshopUser?.firstName || null,
      lastName: webshopUser?.lastName || null,
    };
  });
};

/**
 * Fetcher: loyalty claims from both VCE and HVO
 */
const fetchLoyaltyClaims = async () => {
  const [vceSnapshot, hvoSnapshot] = await Promise.all([
    vceFirestore.collection(FIRESTORE_LOYALTY_CLAIMS_TABLE).get(),
    hvoFirestore.collection(FIRESTORE_LOYALTY_CLAIMS_TABLE).get(),
  ]);

  const vceClaims = vceSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "VCE",
  }));

  const hvoClaims = hvoSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "HVO",
  }));

  return [...vceClaims, ...hvoClaims];
};

/**
 * Fetcher: loyalty rewards from both VCE and HVO
 */
const fetchLoyaltyRewards = async () => {
  const [vceSnapshot, hvoSnapshot] = await Promise.all([
    vceFirestore.collection(FIRESTORE_LOYALTY_REWARDS_TABLE).get(),
    hvoFirestore.collection(FIRESTORE_LOYALTY_REWARDS_TABLE).get(),
  ]);

  const vceRewards = vceSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "VCE",
  }));

  const hvoRewards = hvoSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "HVO",
  }));

  return [...vceRewards, ...hvoRewards];
};

/**
 * Hook to fetch and manage loyalty users from both VCE and HVO Firestore
 */
export const useLoyaltyUsers = (options = {}) => {
  const { refreshInterval = 0 } = options;

  const {
    data: loyaltyUsers,
    error,
    isLoading,
    mutate,
  } = useSWR("loyaltyUsers", fetchLoyaltyUsers, {
    refreshInterval,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  const refreshLoyaltyUsers = useCallback(() => {
    return mutate();
  }, [mutate]);

  return {
    loyaltyUsers: loyaltyUsers || [],
    isLoading,
    error,
    refreshLoyaltyUsers,
  };
};

/**
 * Hook to fetch and manage loyalty claims from both VCE and HVO Firestore
 */
export const useLoyaltyClaims = (options = {}) => {
  const { refreshInterval = 0 } = options;

  const {
    data: loyaltyClaims,
    error,
    isLoading,
    mutate,
  } = useSWR("loyaltyClaims", fetchLoyaltyClaims, {
    refreshInterval,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  const refreshLoyaltyClaims = useCallback(() => {
    return mutate();
  }, [mutate]);

  const getClaimsByUserId = useCallback(
    (userId, shopSource) => {
      if (!loyaltyClaims || !userId) return [];
      return loyaltyClaims
        .filter((claim) => claim.userId === userId && claim.shopSource === shopSource)
        .sort((a, b) => b.dateTime - a.dateTime);
    },
    [loyaltyClaims],
  );

  return {
    loyaltyClaims: loyaltyClaims || [],
    isLoading,
    error,
    refreshLoyaltyClaims,
    getClaimsByUserId,
  };
};

/**
 * Hook to fetch and manage loyalty rewards from both VCE and HVO Firestore
 */
export const useLoyaltyRewards = (options = {}) => {
  const { refreshInterval = 0 } = options;

  const {
    data: loyaltyRewards,
    error,
    isLoading,
    mutate,
  } = useSWR("loyaltyRewards", fetchLoyaltyRewards, {
    refreshInterval,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  const refreshLoyaltyRewards = useCallback(() => {
    return mutate();
  }, [mutate]);

  const findRewardById = useCallback(
    (rewardId, shopSource) => {
      if (!loyaltyRewards || !rewardId) return null;
      return (
        loyaltyRewards.find(
          (reward) => reward.id === rewardId && (!shopSource || reward.shopSource === shopSource),
        ) || null
      );
    },
    [loyaltyRewards],
  );

  /**
   * Create a new loyalty reward in the correct database
   */
  const createReward = useCallback(
    async (rewardData, shopSource) => {
      try {
        const firestore = getFirestore(shopSource);
        const { shopSource: _, ...dataWithoutSource } = rewardData;
        const docRef = await firestore.collection(FIRESTORE_LOYALTY_REWARDS_TABLE).add({
          ...dataWithoutSource,
          visible: true,
        });
        await mutate();
        return docRef.id;
      } catch (err) {
        console.error("Error creating reward:", err);
        throw err;
      }
    },
    [mutate],
  );

  /**
   * Update an existing loyalty reward in the correct database
   */
  const updateReward = useCallback(
    async (rewardId, updates, shopSource) => {
      try {
        const firestore = getFirestore(shopSource);
        const { shopSource: _, ...updatesWithoutSource } = updates;
        await firestore.collection(FIRESTORE_LOYALTY_REWARDS_TABLE).doc(rewardId).update(updatesWithoutSource);
        await mutate();
        return true;
      } catch (err) {
        console.error("Error updating reward:", err);
        throw err;
      }
    },
    [mutate],
  );

  /**
   * Toggle reward visibility (soft delete - never actually delete)
   */
  const toggleRewardVisibility = useCallback(
    async (rewardId, currentVisible, shopSource) => {
      try {
        const firestore = getFirestore(shopSource);
        await firestore
          .collection(FIRESTORE_LOYALTY_REWARDS_TABLE)
          .doc(rewardId)
          .update({ visible: !currentVisible });
        await mutate();
        return true;
      } catch (err) {
        console.error("Error toggling reward visibility:", err);
        throw err;
      }
    },
    [mutate],
  );

  return {
    loyaltyRewards: loyaltyRewards || [],
    isLoading,
    error,
    refreshLoyaltyRewards,
    findRewardById,
    createReward,
    updateReward,
    toggleRewardVisibility,
  };
};
